import { Badge } from './ui/badge';
import { Circle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const About = () => {
  // Technologies the person knows
  const technologies = [{
    name: "React",
    category: "frontend"
  }, {
    name: "TypeScript",
    category: "frontend"
  }, {
    name: "JavaScript",
    category: "frontend"
  }, {
    name: "HTML/CSS",
    category: "frontend"
  }, {
    name: "Tailwind CSS",
    category: "frontend"
  }, {
    name: "Node.js",
    category: "backend"
  }, {
    name: "Express",
    category: "backend"
  }, {
    name: "MongoDB",
    category: "backend"
  }, {
    name: "PostgreSQL",
    category: "backend"
  }, {
    name: "GraphQL",
    category: "backend"
  }, {
    name: "Git",
    category: "tools"
  }, {
    name: "Docker",
    category: "tools"
  }, {
    name: "AWS",
    category: "tools"
  }, {
    name: "Jest",
    category: "tools"
  }, {
    name: "CI/CD",
    category: "tools"
  }];

  // Journey events with updated data format
  const journeyEvents = [{
    year: "2016",
    title: "Computer Science Degree",
    company: "Stanford University",
    description: "Graduated with honors in Computer Science"
  }, {
    year: "2018",
    title: "Junior Developer",
    company: "TechStart Inc.",
    description: "Frontend development with React and JavaScript"
  }, {
    year: "2020",
    title: "Software Engineer",
    company: "Digital Solutions Co.",
    description: "Developed scalable web applications using modern technologies"
  }, {
    year: "2022",
    title: "Senior Developer",
    company: "Tech Innovations Inc.",
    description: "Led team of 5 developers on SaaS platform"
  }, {
    year: "2024",
    title: "Senior Software Engineer",
    company: "Tech Innovations Inc.",
    description: "Leading full-stack development of cloud-native applications"
  }];
  return <section id="about" className="py-20 relative">
      <div className="section-container flex flex-col items-center">
        <h2 className="section-title text-white">About Me</h2>
        
        {/* Profile Section */}
        <div className="flex flex-col items-center mb-16 max-w-xl">
          <div className="w-48 h-48 mb-6 relative">
            <div className="w-full h-full pixel-border bg-forest/80 backdrop-blur-sm p-2">
              <img src="/images/cae085b9-1f96-4432-a0e4-c0055d1e75c6.png" alt="Pixel art avatar of Alex Pixel" className="w-full h-full object-contain animate-pixel-pulse" />
            </div>
            {/* Decorative pixel squares */}
            <div className="absolute -bottom-3 -left-3 w-6 h-6 bg-grass pixel-border animate-float"></div>
            <div className="absolute -top-3 -right-3 w-6 h-6 bg-water pixel-border animate-float" style={{
            animationDelay: '1s'
          }}></div>
          </div>
          
          <div className="text-center mb-8">
            <h3 className="font-pixel text-2xl text-grass-light mb-4">Alex Pixel</h3>
            <p className="font-mono text-sm mb-6 text-white/90">
              Software engineer specializing in frontend development with a passion for pixel-perfect design.
            </p>
            
            <div className="mt-4">
              <a href="#contact" className="pixel-btn bg-forest text-white hover:bg-forest-light">
                Get In Touch
              </a>
            </div>
          </div>
        </div>
        
        {/* Technologies Section */}
        <div className="w-full max-w-4xl mb-16">
          <h3 className="font-pixel text-2xl text-grass-light mb-6 text-center">Technologies</h3>
          <Tabs defaultValue="all" className="w-full">
            <TabsList className="grid grid-cols-4 mb-6 pixel-border bg-white/10 backdrop-blur-md p-1">
              <TabsTrigger value="all" className="font-pixel text-xs text-white">All</TabsTrigger>
              <TabsTrigger value="frontend" className="font-pixel text-xs text-white">Frontend</TabsTrigger>
              <TabsTrigger value="backend" className="font-pixel text-xs text-white">Backend</TabsTrigger>
              <TabsTrigger value="tools" className="font-pixel text-xs text-white">Tools</TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-0">
              <div className="flex flex-wrap gap-2 justify-center">
                {technologies.map((tech, index) => <Badge key={index} className="bg-white/10 backdrop-blur-md pixel-border px-3 py-1 text-white font-mono transition-all hover:translate-y-[-2px] hover:bg-white/20 rounded-3xl">
                    {tech.name}
                  </Badge>)}
              </div>
            </TabsContent>
            
            <TabsContent value="frontend" className="mt-0">
              <div className="flex flex-wrap gap-2 justify-center">
                {technologies.filter(tech => tech.category === "frontend").map((tech, index) => <Badge key={index} className="rounded-md bg-white/10 backdrop-blur-md pixel-border px-3 py-1 text-white font-mono transition-all hover:translate-y-[-2px] hover:bg-white/20">
                      {tech.name}
                    </Badge>)}
              </div>
            </TabsContent>
            
            <TabsContent value="backend" className="mt-0">
              <div className="flex flex-wrap gap-2 justify-center">
                {technologies.filter(tech => tech.category === "backend").map((tech, index) => <Badge key={index} className="rounded-md bg-white/10 backdrop-blur-md pixel-border px-3 py-1 text-white font-mono transition-all hover:translate-y-[-2px] hover:bg-white/20">
                      {tech.name}
                    </Badge>)}
              </div>
            </TabsContent>
            
            <TabsContent value="tools" className="mt-0">
              <div className="flex flex-wrap gap-2 justify-center">
                {technologies.filter(tech => tech.category === "tools").map((tech, index) => <Badge key={index} className="rounded-md bg-white/10 backdrop-blur-md pixel-border px-3 py-1 text-white font-mono transition-all hover:translate-y-[-2px] hover:bg-white/20">
                      {tech.name}
                    </Badge>)}
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* YouTube Video Section */}
        <div className="w-full max-w-4xl mb-16">
          <h3 className="font-pixel text-2xl text-grass-light mb-8 text-center">Teaching & Sharing</h3>
          
          <div className="flex flex-col md:flex-row gap-8 items-center bg-white/5 backdrop-blur-md p-6 rounded-md pixel-border">
            <div className="w-full md:w-1/2 aspect-video">
              <div className="w-full h-full pixel-border overflow-hidden">
                <iframe 
                  width="100%" 
                  height="100%" 
                  src="https://www.youtube.com/embed/IWRS_AM2fiE?si=Iw8td3b67sIyeXe1" 
                  title="Learn JavaScript DOM Manipulation – Tutorial for Beginners" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen 
                  className="aspect-video"
                ></iframe>
              </div>
            </div>
            
            <div className="w-full md:w-1/2 text-white">
              <h4 className="text-xl font-degular font-medium text-grass-light mb-4">Passionate About Teaching</h4>
              <p className="font-mono text-sm mb-4 text-white/80">
                I believe that knowledge is meant to be shared. As a developer, I'm passionate about breaking down complex concepts into digestible pieces that anyone can understand.
              </p>
              <p className="font-mono text-sm mb-4 text-white/80">
                Through my YouTube tutorials, blog posts, and community workshops, I help aspiring developers master modern web technologies and build their confidence in coding.
              </p>
              <p className="font-mono text-sm text-white/80">
                Teaching has not only helped others grow but has deepened my own understanding of software development principles.
              </p>
            </div>
          </div>
        </div>

        {/* Journey Section - Minimalistic Timeline (Karpathy style) */}
        <div className="w-full max-w-4xl">
          <h3 className="font-pixel text-2xl text-grass-light mb-8 text-center">Journey</h3>
          
          <div className="relative bg-white/5 backdrop-blur-md p-8 rounded-md">
            {/* Vertical timeline line */}
            <div className="absolute left-[110px] top-0 bottom-0 w-[1px] bg-white/20"></div>
            
            {/* Journey Items */}
            {journeyEvents.map((event, index) => <div key={index} className="flex mb-14 last:mb-0 group">
                {/* Year column */}
                <div className="w-28 font-mono text-sm text-white/60 pt-1 text-right pr-12">
                  {event.year}
                </div>
                
                {/* Timeline marker */}
                <div className="relative mr-6">
                  <Circle className="w-3 h-3 text-grass-light absolute -left-[6px] top-[6px] fill-grass-light/20 z-10" />
                </div>
                
                {/* Content */}
                <div className="flex-1 pl-6">
                  <h4 className="text-xl font-degular font-medium text-white group-hover:text-grass-light transition-colors">
                    {event.title}
                  </h4>
                  <div className="text-white/80 font-mono text-sm mb-1">
                    {event.company}
                  </div>
                  <p className="text-sm font-mono text-white/60">
                    {event.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
};
export default About;